import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {lazyWithPreload} from '@github-ui/react-core/lazy-with-preload'
import {Spinner} from '@primer/react'
import {Suspense} from 'react'

import type {StartConversationProps} from './StartConversation'

// The comment editor stack (StartConversation → CommentBox → MarkdownEditor / HybridMarkdownEditor
// + CodeMirror + markdown parsing) is heavy and only needed once a user opens a comment box, so we
// load it lazily to keep that chunk off the diff page's initial hydration path. `lazyWithPreload`
// renders the editor synchronously once the chunk has been prefetched (see prefetchStartConversation),
// so opening a comment box after hovering/focusing the line shows no loading fallback.
const StartConversationLazy = lazyWithPreload(async () => ({
  default: (await import('./StartConversation')).StartConversation,
}))

/**
 * Warms the comment-editor chunk ahead of use (e.g. on hover/focus of a diff line) so that opening a
 * comment box renders the editor with no loading fallback. Runs the import at most once.
 */
export function prefetchStartConversation() {
  void StartConversationLazy.preload()
}

/**
 * Drop-in replacement for `StartConversation` that defers loading the comment editor chunk until a
 * comment box opens. Accepts the same props. Renders synchronously when the chunk is already loaded
 * (e.g. prefetched on hover); otherwise shows a brief loading state via Suspense.
 */
export function LazyStartConversation(props: StartConversationProps) {
  return (
    <ErrorBoundary boundaryName="lazy-start-conversation" fallback={<StartConversationLoadError />}>
      <Suspense fallback={<StartConversationLoading />}>
        <StartConversationLazy {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

function StartConversationLoading() {
  return (
    <div role="status" className="d-flex flex-justify-center flex-items-center py-4">
      <Spinner size="small" srText="Loading comment editor" />
    </div>
  )
}

function StartConversationLoadError() {
  return (
    <div role="alert" className="p-3">
      The comment editor failed to load. Try reloading the page.
    </div>
  )
}
